import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {UserResponse} from "../interface/userResponse";
import {UserService} from "../service/user.service";
import {UserCardService} from "../service/user-card.service";
import {UserCardResponse} from "../interface/userCardResponse";
import {HeaderFactoryService} from "../header/header-factory.service";
import {PaymentBackUrlParamsHandlerService} from "../subscription-payment/payment-back-url-params-handler.service";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {SidebarComponent} from "../sidebar/sidebar.component";
import {ViewStateService} from "../service/view-state.service";
import {catchError, concatMap, of, take} from "rxjs";
import {TeamService} from "../service/team.service";
import {ActiveTeamService} from "../service/active-team.service";
import {Team} from "../interface/team";
import {UserCardResponseWrap} from "../interface/userCardResponseWrap";
import {PageableDefault} from "../models/pageableDefault";
import {CardsSortDialogService} from "../cards-sort-dialog/cards-sort-dialog.service";
import {CardsSortUtil} from "../cards-sort-dialog/cards-sort.enum";
import {SubscriptionResponse} from "../interface/subscriptionResponse";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'app-cards',
  templateUrl: './cards.component.html',
  styleUrl: './cards.component.scss'
})
export class CardsComponent implements OnInit {

  user: UserResponse | any
  userCards: UserCardResponse[] | any
  userCardsLength: number | any
  currentProfileImageUrl: string | null = null;
  currentProfileImage: any;
  reservedIdentificationId: string | null = null;

  qrTeamWrappedCards: UserCardResponseWrap[] = [];
  qrUserCreatedTeam: Team | null = null;
  qrTeams: Team[] = [];
  qrActiveTeamIndex: number | null = null;
  qrActiveSubscription?: SubscriptionResponse;
  /** Resolved team entity for team-card UI (owner team or member team). */
  qrTeamForUi?: Team;

  @Output() updatedProfileImageUrl: EventEmitter<string | null> = new EventEmitter<string | null>();

  private successPayment: boolean = false;

  constructor(
    private userService: UserService,
    private userCardService: UserCardService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: SnackBarUtil,
    private translateService: TranslateService,
    private viewStateService: ViewStateService,
    private teamService: TeamService,
    private activeTeamService: ActiveTeamService,
  ) {
    this.successPayment = !!this.router.getCurrentNavigation()?.extras.state?.['successPayment'];
    if (this.successPayment) {
      SidebarComponent.initializer.next(true);
    }

    this.userService.currentUser.subscribe(response => {
      this.user = response
    })
    PaymentBackUrlParamsHandlerService.paymentSuccess.subscribe(() => {
      this.loadCards();
    });

    this.handlePaymentQueryParams();
  }

  ngOnInit(): void {
    if (this.successPayment) {
      this.translateService.get('success_payment_transaction_message').pipe(take(1)).subscribe((message: string) => {
        const data = MockupDataModel.success(message);
        this.showSnackbar(data, SnackBarUtil.CLASSES.success);
      });
    }

    this.reservedIdentificationId = history.state.reservedIdentificationId || null;
    if (this.reservedIdentificationId) {
      this.viewStateService.setListView(true);
    }

    this.loadCards()
    this.getProfileImage(this.user.id);
    this.userService.updatedProfileImageUrl.emit(this.currentProfileImage);
  }

  loadCards(): void {
    this.userCardService.findAll(this.user.id).subscribe(response => {
      this.userCards = response;
      this.userCardsLength = response.length;
      HeaderFactoryService.cardsCount.next(response.length);
      if (this.reservedIdentificationId) {
        this.loadQrTeamCards();
      }
    })
  }

  cardCreatedHandler(createdCard: UserCardResponse): void {
    if (createdCard) {
      if (this.userCards.length > 0) {
        this.userCards.splice(1, 0, createdCard);
      } else {
        this.userCards = [createdCard];
      }
      this.userCardsLength++;
      this.loadCards();
    }
  }

  deleteCardHandler(id: bigint): void {
    if (id) {
      const deletedId: number = Number(id);
      this.userCards = this.userCards.filter((card: { id: bigint }) => Number(card.id) !== deletedId);
      this.userCardsLength--;
      this.loadCards();
    }
  }

  getProfileImage(userId: number): void {
    if (this.user.profilePic != null) {
      this.userService.getProfileImage(BigInt(userId)).subscribe((imageData) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.currentProfileImage = e.target?.result as string;
          this.currentProfileImageUrl = this.currentProfileImage;
          this.userService.setCurrentProfileImageUrl(this.currentProfileImageUrl);
        };
        reader.readAsDataURL(imageData);
      });
    }
  }

  private handlePaymentQueryParams() {
    this.route.queryParams.subscribe((params: Params) => {
      this.removeQueryParamsFromUrl();

      let paymentErrorParam: string | null | undefined = params['paymentError'];
      if(!paymentErrorParam) return;
      const isError = paymentErrorParam === "true";
      const translationKey = isError ? 'error_payment_transaction_message' : 'success_payment_transaction_message';

      this.translateService.get(translationKey).pipe(take(1)).subscribe((message: string) => {
        const data = (isError ? MockupDataModel.error : MockupDataModel.success)(message);
        this.showSnackbar(data, SnackBarUtil.CLASSES[isError ? 'error' : 'success']);
      });
    });
  }

  private removeQueryParamsFromUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    }).catch();
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  private loadQrTeamCards(): void {
    this.fetchQrUserTeam()
      .pipe(concatMap(() => this.fetchQrMemberTeams()))
      .subscribe({
        next: () => {
          const teamId = this.qrActiveTeamIndex ?? this.activeTeamService.getActiveTeamIndex();
          if (!teamId) {
            return;
          }
          const pageable = this.createQrTeamPageable(0);
          this.userCardService.searchCardsByTeamId(Number(teamId), pageable).subscribe(response => {
            const content = response.content as UserCardResponse[];
            this.qrTeamWrappedCards = content.map(c => ({userCardResponse: c}));
            const mine = content.find(c => c.userId === this.user.id);
            this.qrActiveSubscription = mine?.subscription ?? content[0]?.subscription;
            this.qrTeamForUi =
              this.qrUserCreatedTeam ?? this.qrTeams.find(t => t.id === Number(teamId)) ?? undefined;
          });
        }
      });
  }

  private fetchQrUserTeam() {
    return this.teamService.getCurrentUserTeam().pipe(
      concatMap(team => {
        this.qrUserCreatedTeam = team;
        this.qrActiveTeamIndex = team.id;
        this.activeTeamService.setActiveTeamIndex(team.id);
        return of(null);
      }),
      catchError((_error: HttpErrorResponse) => {
        this.qrUserCreatedTeam = null;
        return of(null);
      })
    );
  }

  private fetchQrMemberTeams() {
    return this.teamService.getTeamsByCurrentMember().pipe(
      concatMap(teams => {
        this.qrTeams = teams;
        if (teams.length > 0 && this.qrActiveTeamIndex === null) {
          const [firstTeam] = teams;
          this.qrActiveTeamIndex = firstTeam.id;
          SidebarComponent.activeTeamInitializer.next(this.qrActiveTeamIndex);
          this.activeTeamService.setActiveTeamIndex(this.qrActiveTeamIndex);
        }
        return of(null);
      })
    );
  }

  private createQrTeamPageable(page: number): PageableDefault {
    const sort = CardsSortDialogService.sort.value;
    return new PageableDefault(
      page,
      100,
      CardsSortUtil.getSortFieldBySort(sort),
      CardsSortUtil.getSortDirectionBySort(sort)
    );
  }
}
